import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { SessionService } from '@core/session/store/session.service';
import { User } from '@core/session/models/user';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { SessionQuery } from '@core/session/store/session.query';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { LoginFailedError } from '@backend/api/session/session-api.errors';
import { SupportCenterApiService } from '@backend/api/support-center/support-center-api.service';
import { SupportCenterDialogFactory } from '@features/support-center/components/support-center-dialog/support-center-dialog.factory';

export type LoginMode = 'login' | 'switch' | 'timeout';

@UntilDestroy()
@Component({
    selector: 'rais-login-modal',
    templateUrl: './login-modal.component.html',
    styleUrls: ['./login-modal.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LoginModalComponent implements OnInit, OnDestroy {
  private modalData = inject<{ submittedCallback: (user: User) => void; loginMode: LoginMode }>(NZ_MODAL_DATA);
  private supportCenterApi = inject(SupportCenterApiService);
  private supportCenterDialogFactory = inject(SupportCenterDialogFactory);

  form: UntypedFormGroup;
  isLoggingIn = signal(false);
  showPassword = signal(false);

  supportCenterVisible = signal(false);
  private supportCenterWebFormUrl: string | null = null;
  private supportCenterPortalUrl: string | null = null;
  private supportCenterEmail: string | null = null;
  private supportCenterMaxMessengerEnabled = false;

  togglePasswordVisibility(): void {
    this.showPassword.update(v => !v);
  }

  static LoginModalOpened: boolean;

  constructor(
    fb: UntypedFormBuilder,
    private nzModalRef: NzModalRef,
    private sessionQuery: SessionQuery,
    private sessionService: SessionService,
    private nzNotificationService: NzNotificationService,
  ) {
    this.form = fb.group({
      login: ['', Validators.required],
      password: ['', Validators.required],
    });

    LoginModalComponent.LoginModalOpened = true;

    this.form.get('password')!.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(v => { if (!v) this.showPassword.set(false); });
  }

  onSubmit() {
    this.isLoggingIn.set(true);
    this.sessionService
      .auth({
        ...this.form.value,
      })
      .pipe(finalize(() => this.isLoggingIn.set(false)))
      .subscribe({
        next: (session) => {
          this.modalData.submittedCallback(session.user);
          this.nzNotificationService.success('Вход в систему', 'Успешный вход в систему', {
            nzKey: 'login-success-notification',
            nzClass: 'login-success-notification',
            nzDuration: 9999
          });
          this.nzModalRef.close();
        },
        error: (error) => {
          if (error instanceof LoginFailedError) {
            this.nzNotificationService.error('Вход в систему', error.message);
            return;
          }

          throw error;
        },
      });
  }

  ngOnInit(): void {
    this.supportCenterApi
      .getPublicConfiguration()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (config) => {
          if (!config.enabled) return;
          this.supportCenterWebFormUrl = config.webFormUrl ?? null;
          this.supportCenterPortalUrl = config.portalUrl ?? null;
          this.supportCenterEmail = config.supportEmail ?? null;
          this.supportCenterMaxMessengerEnabled = config.maxMessengerEnabled ?? false;
          this.supportCenterVisible.set(true);
        },
      });
  }

  openSupportCenter(): void {
    this.supportCenterDialogFactory.open({
      webFormUrl: this.supportCenterWebFormUrl,
      portalUrl: this.supportCenterPortalUrl,
      supportEmail: this.supportCenterEmail,
      maxMessengerEnabled: this.supportCenterMaxMessengerEnabled,
    });
  }

  ngOnDestroy(): void {
    LoginModalComponent.LoginModalOpened = false;
  }

}
